/**
 * (c) 2026, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import {
  CSSProperties,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import { Svg, SystemStyleObject, token } from "@microbit/ui";
import { icons, Off } from "../../utils/icons";

interface HandHoldingMicrobitProps {
  /** Per-instance style overrides, merged after the base. */
  css?: SystemStyleObject;
  style?: CSSProperties;
}

export interface HandHoldingMicrobitRef {
  displayHappyLed(): void;
  show(): void;
  reset(): void;
}

const ledDim = 5; // 5-by-5 led grid.
const ledSpacing = 10; // Distance between led.

const HandHoldingMicrobit = forwardRef<
  HandHoldingMicrobitRef,
  HandHoldingMicrobitProps
>(function HandHoldingMicrobit(
  { css: cssProp, style }: HandHoldingMicrobitProps,
  ref
) {
  const litLedColor = token("colors.pink.500");
  const unlitLedColor = token("colors.gray.200");
  const [ledPattern, setLedPattern] = useState<string>(Off);
  const [visible, setVisible] = useState<boolean>(false);
  useImperativeHandle(
    ref,
    () => {
      return {
        displayHappyLed() {
          setLedPattern(icons.Happy);
        },
        show() {
          setVisible(true);
        },
        reset() {
          setVisible(false);
          setLedPattern(Off);
        },
      };
    },
    []
  );
  return (
    <Svg
      viewBox="0 0 194.7 200.05"
      css={{
        w: "100%",
        position: "absolute",
        opacity: visible ? 1 : 0,
        ...cssProp,
      }}
      style={style}
    >
      {[...ledPattern].map((lit, i) => (
        <circle
          // eslint-disable-next-line @eslint-react/no-array-index-key -- fixed list, position is identity
          key={i}
          cx={102.47 + (i % ledDim) * ledSpacing}
          cy={39.07 + Math.floor(i / ledDim) * ledSpacing}
          r={parseInt(lit) ? 3 : 1.5}
          fill={parseInt(lit) ? litLedColor : unlitLedColor}
        />
      ))}
      <path
        fill="#010101"
        d="M191.3,3.66c-2.2-2.2-5.12-3.42-8.23-3.43l-123.59-.24h-.02c-3.1,0-6.02,1.21-8.22,3.4-2.2,2.2-3.42,5.12-3.43,8.23l-.14,72.69c-14.97,13.32-21.59,23.58-24.49,30.25-4.78,10.88-7.2,20.89-7.19,29.77,0,2.93.27,5.77.8,8.45L.59,168.99c-.78.78-.78,2.05,0,2.83.39.39.9.59,1.41.59s1.02-.2,1.41-.59l16.98-16.98c.49-.49.69-1.19.54-1.86-.62-2.71-.93-5.62-.93-8.65,0-8.32,2.3-17.79,6.86-28.17,3.33-7.65,11.92-20.37,33.89-37.52.18-.14.33-.26.47-.37,1.05.79,3.07,3.04,3.77,6.12.69,3.06-.23,5.88-2.71,8.36l-15.49,15.22c-.38.37-.6.88-.6,1.42,0,.53.21,1.05.59,1.42,5.6,5.6,2.56,20.62-3.8,26.98-.78.78-.78,2.05,0,2.83.78.78,2.05.78,2.83,0,3.61-3.61,6.5-9.74,7.53-15.99.47-2.82.51-5.47.19-7.88,1.73.99,3.7,1.53,5.75,1.54h4.21c.67.03,1.32-.34,1.68-.91.54-.83,1.37-1.4,2.33-1.61.97-.21,1.96-.03,2.79.51.45.29.82.67,1.11,1.12.11.17.24.31.39.43l-.18.18c-3.51,3.51-3.51,9.21,0,12.71,1.46,1.46,3.31,2.3,5.21,2.55-.62,1.24-.96,2.61-.96,4.04,0,2.4.94,4.66,2.63,6.36,1.47,1.47,3.34,2.32,5.26,2.55-.51,1.14-.79,2.38-.79,3.67,0,2.4.94,4.66,2.63,6.36,1.17,1.17,2.58,1.95,4.07,2.34l-11.08,9.85s-.06.05-.08.08c-7.98,7.98-19.4,11.57-30.55,9.6-.64-.11-1.3.09-1.76.56l-17.96,17.96c-.78.78-.78,2.05,0,2.83.39.39.9.59,1.41.59s1.02-.2,1.41-.59l17.24-17.24c12.08,1.8,24.34-2.21,32.99-10.84l16.27-14.47c.26-.21.5-.43.74-.66h0s14.9-14.91,14.9-14.91c1.7-1.7,2.63-3.96,2.63-6.36s-.94-4.66-2.63-6.36c-1.44-1.44-3.28-2.32-5.26-2.56,1.08-2.44,1.03-5.27-.18-7.68l8.48.02h0c.68,0,1.31-.34,1.68-.92.54-.83,1.37-1.4,2.33-1.61.97-.21,1.96-.03,2.79.51.45.29.82.67,1.11,1.12.37.57,1,.92,1.68.92l16.79.03h0c.68,0,1.31-.34,1.68-.92,1.11-1.72,3.41-2.21,5.12-1.1.45.29.82.67,1.11,1.12.37.57,1,.92,1.68.92l16.79.03h0c.68,0,1.31-.34,1.68-.92.54-.83,1.37-1.4,2.33-1.61.97-.21,1.96-.03,2.79.51.44.29.83.67,1.11,1.12.37.57,1,.92,1.68.92h4.21s.02,0,.02,0c3.1,0,6.02-1.21,8.22-3.4,2.2-2.2,3.42-5.12,3.43-8.23l.18-95.01c0-3.11-1.2-6.04-3.4-8.24ZM64.86,95.83l.22-.22c.22-.22.42-.43.61-.65.74-.59,1.65-.93,2.61-.93h0c1.13,0,2.18.44,2.98,1.24.79.8,1.23,1.86,1.23,2.98h0c0,1.13-.44,2.18-1.24,2.98-.8.79-1.85,1.23-2.97,1.23h0c-2.32,0-4.21-1.9-4.21-4.22,0-.88.27-1.7.77-2.41ZM74.42,120.85l2.52-2.52,12.92.02h0c.68,0,1.31-.34,1.68-.92.48-.74,1.18-1.23,1.95-1.49-.04.04-.08.07-.12.1l-11.61,11.61c-.1.07-.2.15-.29.24-1.95,1.95-5.11,1.95-7.06,0s-1.95-5.11,0-7.06ZM111.84,134.99c0,1.33-.52,2.59-1.46,3.53l-14.86,14.86-.53.47c-.87.67-1.93,1.03-3.04,1.03-1.33,0-2.59-.52-3.53-1.46-.94-.94-1.46-2.2-1.46-3.53s.52-2.59,1.46-3.53l2.03-2.03c.26-.21.51-.43.75-.66.11-.11.2-.24.28-.37l11.85-11.84c.94-.94,2.2-1.46,3.53-1.46s2.59.52,3.53,1.46c.94.94,1.46,2.2,1.46,3.53ZM103.27,118.89c1.95,1.95,1.95,5.11,0,7.06l-2.03,2.03c-.26.21-.51.43-.75.66l-12.58,12.58c-1.96,1.55-4.8,1.44-6.61-.37-.94-.94-1.46-2.2-1.46-3.53s.52-2.59,1.46-3.53l14.9-14.9c.53-.53,1.15-.9,1.81-1.14.37.4.89.63,1.44.64h3.19c.21.16.42.32.61.51ZM190.52,106.91c0,4.21-3.44,7.63-7.65,7.63h-3.23c-.47-.54-1.02-1.02-1.62-1.41-1.73-1.12-3.79-1.49-5.8-1.06-1.63.35-3.08,1.2-4.16,2.44l-14.81-.03c-.47-.54-1.01-1.01-1.62-1.4-3.24-2.09-7.48-1.44-9.96,1.38l-14.81-.03c-.47-.54-1.01-1.01-1.62-1.4-1.73-1.12-3.79-1.49-5.8-1.06-1.63.35-3.08,1.2-4.16,2.44l-14.81-.03c-.47-.54-1.01-1.01-1.62-1.4-1.73-1.12-3.79-1.49-5.8-1.06-1.63.35-3.08,1.2-4.16,2.44l-14.81-.03c-.47-.54-1.01-1.01-1.62-1.4-1.73-1.12-3.79-1.49-5.8-1.06-1.63.35-3.08,1.2-4.16,2.44h-3.22c-2.04-.01-3.96-.81-5.4-2.26-.97-.98-1.65-2.18-1.98-3.48l9.31-9.15c.57,3.41,3.53,6.02,7.1,6.03h.01c1.92,0,3.73-.75,5.09-2.1,1.37-1.36,2.12-3.17,2.12-5.1,0-1.93-.74-3.74-2.1-5.11s-3.17-2.12-5.1-2.12h-.01s-.04,0-.06,0c1.07-2.54,1.08-4.92.78-6.74-.49-2.94-2.08-6-4.25-8.18-3.11-3.11-4.9-2-5.48-1.42-.13.12-.49.39-1.02.8-2.34,1.83-4.53,3.61-6.59,5.34l.08-41.23L87.7,4.05h.26s-.1,17.93-.1,17.93l18.18-17.89h.14s-.05,8.34-.05,8.34l8.35-8.33,68.58.13c2.04,0,3.96.8,5.4,2.25,1.44,1.45,2.23,3.37,2.23,5.41l-.18,95.02Z"
      />
      <path
        fill="#010101"
        d="M173.89,91.23h-.01c-3.97,0-7.21,3.23-7.21,7.2,0,3.98,3.22,7.22,7.2,7.23h.01c1.92,0,3.73-.75,5.09-2.1,1.37-1.36,2.12-3.17,2.12-5.1h0c0-1.93-.74-3.74-2.1-5.11s-3.17-2.12-5.1-2.12ZM173.88,102.66h0c-2.32,0-4.21-1.9-4.21-4.22,0-2.32,1.89-4.21,4.21-4.21h0c1.13,0,2.18.44,2.98,1.24.79.8,1.23,1.86,1.23,2.98,0,2.32-1.89,4.21-4.21,4.21Z"
      />
      <path
        fill="#010101"
        d="M147.5,91.18h-.01c-3.97,0-7.21,3.23-7.22,7.2,0,1.93.74,3.74,2.1,5.11,1.36,1.37,3.17,2.12,5.1,2.12h.01c1.92,0,3.73-.75,5.09-2.1,1.37-1.36,2.12-3.17,2.12-5.1h0c0-3.98-3.22-7.22-7.2-7.23ZM150.46,101.38c-.8.79-1.85,1.23-2.97,1.23h0c-2.32,0-4.21-1.9-4.21-4.22,0-2.32,1.89-4.21,4.21-4.21h0c2.32,0,4.21,1.9,4.21,4.22,0,1.13-.44,2.18-1.24,2.98Z"
      />
      <path
        fill="#010101"
        d="M121.11,91.13h-.01c-3.97,0-7.21,3.23-7.22,7.2,0,3.98,3.22,7.22,7.2,7.23h.01c1.92,0,3.73-.75,5.09-2.1,1.37-1.36,2.12-3.17,2.12-5.1,0-3.98-3.22-7.22-7.2-7.23ZM121.09,102.56h0c-1.13,0-2.18-.44-2.98-1.24-.79-.8-1.23-1.86-1.23-2.98,0-2.32,1.9-4.21,4.22-4.21h0c2.32,0,4.21,1.9,4.21,4.22,0,2.32-1.89,4.21-4.22,4.21Z"
      />
      <path
        fill="#010101"
        d="M94.71,91.08h-.01c-1.92,0-3.73.75-5.09,2.1-1.37,1.36-2.12,3.17-2.12,5.1,0,1.93.74,3.74,2.1,5.11,1.36,1.37,3.17,2.12,5.1,2.12h.01c3.97,0,7.21-3.23,7.22-7.2h0c0-3.98-3.22-7.22-7.2-7.23ZM94.7,102.51h0c-1.13,0-2.18-.44-2.98-1.24-.79-.8-1.23-1.86-1.23-2.98,0-1.13.44-2.18,1.24-2.98.8-.79,1.85-1.23,2.98-1.23h0c2.32,0,4.21,1.9,4.21,4.22,0,2.32-1.9,4.21-4.22,4.21Z"
      />
      <path
        fill="#010101"
        d="M180.83,50.29l-13.72-.03c-1.22,0-2.23,1-2.23,2.23l-.03,13.72c0,1.23,1,2.23,2.23,2.23l13.72.03h0c1.23,0,2.23-1,2.23-2.23l.03-13.72c0-1.23-1-2.23-2.23-2.23ZM178.53,59.38c0,2.52-2.06,4.56-4.57,4.56h0c-2.52,0-4.57-2.06-4.56-4.58,0-2.52,2.06-4.56,4.57-4.56h0c2.52,0,4.57,2.06,4.57,4.58h0Z"
      />
      <path
        fill="#010101"
        d="M75.26,50.61l-13.72-.02c-.95,0-1.71.77-1.72,1.71l-.03,13.72c0,.95.77,1.71,1.71,1.72l13.72.02h0c.95,0,1.71-.77,1.72-1.71l.03-13.72c0-.95-.77-1.71-1.71-1.72ZM72.96,59.18c0,1.22-.48,2.37-1.35,3.23-.86.86-2.01,1.33-3.23,1.33h-.01c-1.22,0-2.37-.48-3.23-1.35-.86-.87-1.34-2.02-1.33-3.24,0-1.22.48-2.37,1.35-3.23.87-.86,2.02-1.33,3.24-1.33,2.52,0,4.57,2.06,4.57,4.58h0Z"
      />
      <path
        fill="#010101"
        d="M126.32,18.84c-.75.01-1.35.62-1.36,1.37,0,.77.63,1.39,1.41,1.39.76-.01,1.37-.64,1.36-1.41-.01-.76-.64-1.37-1.41-1.36Z"
      />
      <path
        fill="#010101"
        d="M116,18.78h0c-.76.01-1.37.64-1.35,1.41.01.76.65,1.37,1.41,1.35.75-.01,1.35-.62,1.35-1.37,0-.77-.63-1.39-1.41-1.39Z"
      />
      <path
        fill="#010101"
        d="M126.54,13.38l-10.51-.06c-.1,0-.2,0-.3,0-3.78.06-6.79,3.17-6.73,6.95.06,3.78,3.17,6.79,6.95,6.73l10.52.06c.11,0,.22,0,.33,0,3.78-.07,6.78-3.19,6.71-6.97-.07-3.78-3.19-6.78-6.97-6.71ZM126.48,24.32l-10.52-.06c-.06,0-.13,0-.19,0-2.27-.07-4.05-1.95-3.98-4.22.07-2.27,1.95-4.05,4.22-3.98l10.51.06c.07,0,.15,0,.22,0,2.27.07,4.04,1.97,3.97,4.24s-1.97,4.04-4.24,3.97Z"
      />
    </Svg>
  );
});

export default HandHoldingMicrobit;
